'use strict';

const libPictView = require('pict-view');

/**
 * DocEditor - the host chrome (save / back) around the rich markdown editor
 * (PictView-RichDocEditor = pict-section-markdowneditor). The body is a document
 * in the Docs Lake, addressed by PATH - not a ContentDocument row. The editor
 * mounts into #DocEditor-EditorHost, bound to AppData.RichDocEditor.Segments.
 *
 * Reached via app.showDocEditor(ownerType, idOwner, docPath, headerLabel). The
 * path must already exist in the lake; EntityDetail mints the path and its
 * DocEntityLink before opening the editor, so the editor only ever edits bytes.
 * The doc's title is its first H1 (no separate field). Images and drawings are
 * owned by the doc's entity (ownerType, idOwner), so they survive in the gallery.
 */
class PictViewDocEditor extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._ownerType = '';
		this._idOwner = 0;
		this._docPath = '';
		this._saveTimer = null;
		this.pict.AppData.DocEditor = this.pict.AppData.DocEditor || { HeaderTitle: '', DocPath: '' };
		this.pict.AppData.RichDocEditor = this.pict.AppData.RichDocEditor || { Segments: [] };
	}

	_editorView() { return this.pict.views['Plansheet-RichDocEditor']; }

	/** The doc's owner entity - used by the rich editor for image / drawing ownership. */
	ownerContext() { return { OwnerType: this._ownerType, IDOwner: this._idOwner }; }

	showEditor(pOwnerType, pIDOwner, pDocPath, pHeaderLabel)
	{
		this._ownerType = pOwnerType;
		this._idOwner = Number(pIDOwner);
		this._docPath = String(pDocPath || '');

		let tmpState = this.pict.AppData.DocEditor;
		tmpState.DocPath = this._docPath;
		tmpState.HeaderTitle = pHeaderLabel || 'Edit document';

		this.pict.providers.DocLakeAPI.readDoc(this._docPath).then((pBody) =>
		{
			this.pict.AppData.RichDocEditor.Segments = [{ Content: pBody || '' }];
			this.render();
		});
	}

	/** Mount (or remount) the rich editor into the host container after render. */
	_mountEditor()
	{
		let tmpEditor = this._editorView();
		if (!tmpEditor)
		{
			this.pict.ContentAssignment.assignContent('#DocEditor-EditorHost',
				'<div style="padding:24px;color:var(--theme-color-text-secondary,#5b6376)">The rich editor is not available in this build.</div>');
			return;
		}
		try { if (tmpEditor.initialRenderComplete) { tmpEditor.destroy(); tmpEditor.initialRenderComplete = false; } } catch (pErr) { /* ignore */ }
		tmpEditor.render();
		tmpEditor.marshalToView();
		if (typeof tmpEditor.setPreviewMode === 'function') { tmpEditor.setPreviewMode('side'); }
	}

	/** Debounced autosave on any editor change. */
	onEditorChanged()
	{
		if (this._saveTimer) { clearTimeout(this._saveTimer); }
		this._saveTimer = setTimeout(() => { this._saveTimer = null; this._persist(true); }, 1200);
	}

	_persist(pSilent)
	{
		if (!this._docPath) { return; }
		let tmpEditor = this._editorView();
		let tmpBody = (tmpEditor && typeof tmpEditor.getAllContent === 'function') ? tmpEditor.getAllContent('\n\n') : '';
		return this.pict.providers.DocLakeAPI.saveDoc(this._docPath, tmpBody)
			.then(() => { if (!pSilent) { this._toast('Document saved.', 'success'); } })
			.catch((pErr) => { if (!pSilent) { this._toast('Save failed: ' + (pErr.message || pErr), 'error'); } });
	}

	save()
	{
		if (this._saveTimer) { clearTimeout(this._saveTimer); this._saveTimer = null; }
		let tmpResult = this._persist(false);
		if (tmpResult && typeof tmpResult.then === 'function') { tmpResult.then(() => this.back()); }
		else { this.back(); }
	}

	back()
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showEntityDetail === 'function') { tmpApp.showEntityDetail(this._ownerType, this._idOwner); }
	}

	_toast(pMessage, pType)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function')
		{
			tmpModal.toast(pMessage, { type: pType || 'info', duration: (pType === 'error') ? 6000 : 2500 });
		}
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		this._mountEditor();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = PictViewDocEditor;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-DocEditor',
	DefaultRenderable: 'DocEditor-Display',
	AutoRender: false,
	CSS: /*css*/`
		.ps-doc { padding: 16px 20px; height: 100%; display: flex; flex-direction: column; box-sizing: border-box; }
		.ps-doc-head { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
		.ps-doc-head h2 { font-size: 18px; font-weight: 600; color: var(--theme-color-text-primary, #2c3140); margin: 0; flex: 1; }
		.ps-doc-path { font-size: 12px; color: var(--theme-color-text-secondary, #5b6376); margin: -4px 0 10px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
		.ps-doc-host { flex: 1; min-height: 0; display: flex; border: 1px solid var(--theme-color-border-default, #e6e9ee); border-radius: 8px; overflow: hidden; }
		.ps-doc-host > * { flex: 1; min-width: 0; }
		.pict-mde-button-drawing { font-size: 14px; }
	`,
	Templates:
	[
		{
			Hash: 'DocEditor-Display',
			Template: /*html*/`
<div class="ps-doc">
	<div class="ps-doc-head">
		<button class="ps-btn" onclick="_Pict.views['Plansheet-DocEditor'].back()">&larr; Back</button>
		<h2>{~D:AppData.DocEditor.HeaderTitle~}</h2>
		<button class="ps-btn ps-btn-primary" onclick="_Pict.views['Plansheet-DocEditor'].save()">Save</button>
	</div>
	<div class="ps-doc-path">{~D:AppData.DocEditor.DocPath~}</div>
	<div class="ps-doc-host" id="DocEditor-EditorHost"></div>
</div>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'DocEditor-Display',
			TemplateHash: 'DocEditor-Display',
			ContentDestinationAddress: '#Plansheet-Workspace'
		}
	]
};
